import {
  Col,
  Flex,
  Form,
  Input,
  Paragraph,
  Row,
  Select,
  Switch,
  Textarea,
  type FormInstance,
  type SelectOption,
} from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { useCallback, useEffect, useMemo } from "react";
import { createProductKeyRules } from "./productKeyValidation";

export const PRODUCT_CATEGORY_REQUIRED_MESSAGE =
  "Product Category is required.";

export const PRODUCT_UOM_REQUIRED_MESSAGE = "UOM is required.";

export interface ProductAddEditFormValues {
  itemName: string;
  productKey: string;
  categoryId: number | null;
  uomId: number | null;
  itemDescription: string;
  upcItemNumber: string;
  glItemNumber: string;
  glItemDescription: string;
  isActive: boolean;
}

export interface ProductCategorySelectOption {
  productCategoryId: number;
  name: string;
  categoryKey: string;
}

export interface ProductUomSelectOption {
  uomId: number;
  uomName: string;
  uomCode: string;
}

interface MasterDataProductAddEditFormProps {
  form: FormInstance<ProductAddEditFormValues>;
  mode: "create" | "edit";
  initialValues: ProductAddEditFormValues;
  categoryOptions: ProductCategorySelectOption[];
  categoriesLoading?: boolean;
  uomOptions: ProductUomSelectOption[];
  uomsLoading?: boolean;
  onSubmit: (values: ProductAddEditFormValues) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

function parseCategoryIdFromSelect(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "object" && "value" in (value as object)) {
    const raw = (value as SelectOption).value;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

interface IdSelectFieldProps {
  value?: number | null;
  onChange?: (value: number | null) => void;
  onBlur?: (...args: unknown[]) => void;
  isError?: boolean;
  options: SelectOption[];
  isLoading?: boolean;
  selectKey: string;
  placeholder: string;
  ariaLabel: string;
}

const IdSelectField = ({
  value,
  onChange,
  onBlur,
  isError = false,
  options,
  isLoading = false,
  selectKey,
  placeholder,
  ariaLabel,
}: IdSelectFieldProps) => {
  const selected = options.find((option) => option.value === String(value));
  return (
    <Select
      key={`${selectKey}-${value ?? "none"}-${options.length}`}
      size="medium"
      isSearchable
      isClearable={false}
      isLoading={isLoading}
      placeholder={placeholder}
      options={options}
      defaultValue={selected}
      isError={isError}
      onChange={(next) => onChange?.(parseCategoryIdFromSelect(next))}
      onMenuClose={() => {
        if (!isLoading) {
          onBlur?.();
        }
      }}
      aria-label={ariaLabel}
    />
  );
};

export const MasterDataProductAddEditForm = ({
  form,
  mode,
  initialValues,
  categoryOptions,
  categoriesLoading = false,
  uomOptions,
  uomsLoading = false,
  onSubmit,
  onFormValidityChange,
}: MasterDataProductAddEditFormProps) => {
  const selectOptions = useMemo<SelectOption[]>(
    () =>
      categoryOptions.map((category) => ({
        value: String(category.productCategoryId),
        label: category.name,
      })),
    [categoryOptions],
  );

  const uomSelectOptions = useMemo<SelectOption[]>(
    () =>
      uomOptions.map((uom) => ({
        value: String(uom.uomId),
        label: `${uom.uomName} (${uom.uomCode})`,
      })),
    [uomOptions],
  );

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);

    const values = form.getFieldsValue();
    const itemName = values.itemName?.trim() ?? "";
    const productKey = values.productKey?.trim() ?? "";
    const itemDescription = values.itemDescription?.trim() ?? "";
    const upcItemNumber = values.upcItemNumber?.trim() ?? "";
    const glItemNumber = values.glItemNumber?.trim() ?? "";
    const glItemDescription = values.glItemDescription?.trim() ?? "";
    const categoryId = values.categoryId;
    const uomId = values.uomId;

    const hasRequiredValues =
      itemName !== "" &&
      productKey !== "" &&
      itemDescription !== "" &&
      upcItemNumber !== "" &&
      categoryId != null &&
      Number(categoryId) > 0 &&
      uomId != null &&
      Number(uomId) > 0;

    const isDirty =
      mode === "create"
        ? true
        : itemName !== (initialValues.itemName?.trim() ?? "") ||
          productKey !== (initialValues.productKey?.trim() ?? "") ||
          itemDescription !== (initialValues.itemDescription?.trim() ?? "") ||
          upcItemNumber !== (initialValues.upcItemNumber?.trim() ?? "") ||
          glItemNumber !== (initialValues.glItemNumber?.trim() ?? "") ||
          glItemDescription !==
            (initialValues.glItemDescription?.trim() ?? "") ||
          Number(categoryId) !== Number(initialValues.categoryId) ||
          Number(uomId) !== Number(initialValues.uomId) ||
          Boolean(values.isActive) !== Boolean(initialValues.isActive);

    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [form, initialValues, mode, onFormValidityChange]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, updateFormValidity]);

  const itemNameRules = useMemo(
    () => [
      { required: true, message: "Product name is required" },
      {
        max: 255,
        message: "Product name must be less than or equal to 255 characters.",
      },
    ],
    [],
  );

  const productKeyRules = useMemo(() => createProductKeyRules(), []);

  const itemDescriptionRules = useMemo(
    () => [
      { required: true, message: "Product description is required" },
      {
        max: 500,
        message:
          "Product description must be less than or equal to 500 characters.",
      },
    ],
    [],
  );

  const glItemDescriptionRules = useMemo(
    () => [
      {
        max: 500,
        message:
          "GL Item Description must be less than or equal to 500 characters.",
      },
    ],
    [],
  );

  const upcRules = useMemo(
    () => [
      { required: true, message: "UPC is required" },
      {
        max: 255,
        message: "UPC must be less than or equal to 255 characters.",
      },
    ],
    [],
  );

  const glItemNumberRules = useMemo(
    () => [
      {
        max: 255,
        message: "GL Item # must be less than or equal to 255 characters.",
      },
    ],
    [],
  );

  const categoryRules = useMemo(
    () => [
      { required: true, message: PRODUCT_CATEGORY_REQUIRED_MESSAGE },
      {
        validator: async (_: unknown, value: unknown) => {
          if (value == null || Number(value) <= 0) {
            throw new Error(PRODUCT_CATEGORY_REQUIRED_MESSAGE);
          }
        },
      },
    ],
    [],
  );

  const uomRules = useMemo(
    () => [
      { required: true, message: PRODUCT_UOM_REQUIRED_MESSAGE },
      {
        validator: async (_: unknown, value: unknown) => {
          if (value == null || Number(value) <= 0) {
            throw new Error(PRODUCT_UOM_REQUIRED_MESSAGE);
          }
        },
      },
    ],
    [],
  );

  return (
    <ErrorBoundary>
      <Form
        form={form}
        onFinish={onSubmit}
        onFieldsChange={updateFormValidity}
        className="master-data-product-add-edit"
      >
        <Row>
          <Col span={6}>
            <Form.Item
              name="itemName"
              label="Product Name"
              validateTrigger={["onChange", "onBlur"]}
              rules={itemNameRules}
            >
              <Input placeholder="Product name" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="productKey"
              label="Product Key"
              validateTrigger={["onChange", "onBlur"]}
              rules={productKeyRules}
            >
              <Input placeholder="Product key" />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={6}>
            <Form.Item
              name="categoryId"
              label="Product Category"
              validateTrigger={["onChange", "onBlur"]}
              validateFirst
              rules={categoryRules}
            >
              <IdSelectField
                options={selectOptions}
                isLoading={categoriesLoading}
                selectKey={`category-select-${mode}-${initialValues.categoryId ?? "new"}`}
                placeholder="Select category"
                ariaLabel="Product category"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="upcItemNumber"
              label="UPC"
              validateTrigger={["onChange", "onBlur"]}
              rules={upcRules}
            >
              <Input placeholder="UPC" />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={6}>
            <Form.Item
              name="itemDescription"
              label="Product Description"
              validateTrigger={["onChange", "onBlur"]}
              rules={itemDescriptionRules}
            >
              <Textarea placeholder="Product description" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="uomId"
              label="UOM"
              validateTrigger={["onChange", "onBlur"]}
              validateFirst
              rules={uomRules}
            >
              <IdSelectField
                options={uomSelectOptions}
                isLoading={uomsLoading}
                selectKey={`uom-select-${mode}-${initialValues.uomId ?? "new"}`}
                placeholder="Select UOM"
                ariaLabel="Product UOM"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={6}>
            <Form.Item
              name="glItemNumber"
              label="GL Item #"
              validateTrigger={["onChange", "onBlur"]}
              rules={glItemNumberRules}
            >
              <Input placeholder="Optional GL item number" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              name="glItemDescription"
              label="GL Item Description"
              validateTrigger={["onChange", "onBlur"]}
              rules={glItemDescriptionRules}
            >
              <Textarea placeholder="Optional GL item description" />
            </Form.Item>
          </Col>
          <Col span={2}>
            <Flex
              align="start"
              gap={1}
              className="status-switch"
              direction="column"
            >
              <Paragraph className="m-0 custom-label">Status</Paragraph>
              <Form.Item name="isActive" valuePropName="isSelected" noStyle>
                <Switch
                  className="p-0 mt-1"
                  size="small"
                  aria-label="Product active status"
                />
              </Form.Item>
            </Flex>
          </Col>
        </Row>
      </Form>
    </ErrorBoundary>
  );
};
